import React, { useContext, useState } from 'react';
import {
  Grid,
  TextField,
  MenuItem,
  InputAdornment,
  SvgIcon,
  Select,
  Button,
  FormControlLabel,
  Switch,
  Checkbox,
  ListItemText,
  SelectChangeEvent,
  FormControl,
  InputLabel,
  Box,
} from '@mui/material';
import { theme } from '../../../theme';
import { useTranslation } from 'react-i18next';
import { GetStockResponse } from '../../../app/services/api/generated';
import { StockFilterOptions } from './interfaces/StockFilterOptions';
import StockOnHandTable from './StockOnHandTable';
import { SearchSm } from '../../../assets';
import { UserContext } from '../../../components/shared/useUser';
import LoadingWheel from '../../../components/ui/LoadingWheel';
import useLogError from '../../../hooks/useLogError';
import { MultiStorePicker, StoreSelection } from '../../../components/shared/MultiStorePicker';

interface StockOnHandBoxProps {
  stockOnHands: GetStockResponse;
  loadData: (options: StockFilterOptions) => void;
}

const StockOnHandBox = (props: StockOnHandBoxProps) => {
  const { t } = useTranslation('common');
  const { logError } = useLogError();
  const DEFAULT_QUERY = {
    sort: 'itemNumber',
    take: 10,
    skip: 0,
    direction: 'asc' as const,
  };

  const { user, hasPermissionTo } = useContext(UserContext);
  const [categoryFilters, setCategoryFilters] = useState('');
  const [stockFilters, setStockFilters] = useState('');
  const [storeSelection, setStoreSelection] = useState<StoreSelection>({ mode: 'all' });
  const [category, setCategory] = React.useState<string[]>([]);
  const [stock, setStock] = React.useState<string[]>([]);
  const [search, setSearch] = useState('');
  const [hasRecentActivity, setHasRecentActivity] = useState(true);
  const [isMadeInStore, setIsMadeInStore] = useState<boolean>(false);
  const [isNew, setIsNew] = useState<boolean>(false);
  const [isLoading, setIsLoading] = useState<boolean>(false);
  const [searchTrigger, setSearchTrigger] = useState(0); // increments only when a search is executed
  const hasPermissionToReadMadeInStore = hasPermissionTo(['MadeInStoreRead']);

  const ITEM_HEIGHT = 48;
  const ITEM_PADDING_TOP = 8;
  const MenuProps = {
    PaperProps: {
      style: {
        maxHeight: ITEM_HEIGHT * 4.5 + ITEM_PADDING_TOP,
        width: 250,
      },
    },
  };

  const stockLevelOptions = [
    { id: 'positivestock', name: t('stockOnHand.positiveStock') },
    { id: 'negativestock', name: t('stockOnHand.negativeStock') },
    { id: 'belowminimumquantity', name: t('stockOnHand.belowMinimumQuantity') },
    { id: 'outofstock', name: t('stockOnHand.outOfStock') },
  ];
  const handleSearchChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    const newSearchValue = event.target.value;
    setSearch(newSearchValue);
  };

  const handleSearchKeyPress = (e: React.KeyboardEvent<HTMLInputElement>) => {
    if (e.key === 'Enter') {
      searchCall();
    }
  };

  const handleCategoryChange = (event: SelectChangeEvent<typeof category>) => {
    const {
      target: { value },
    } = event;

    setCategory(typeof value === 'string' ? value.split(',') : value);
    setCategoryFilters(typeof value === 'string' ? value : value.join(','));
  };

  const handleStockLevelChange = (event: SelectChangeEvent<typeof stock>) => {
    const {
      target: { value },
    } = event;
    setStock(typeof value === 'string' ? value.split(',') : value);

    if (typeof value === 'string') {
      setStockFilters(value);
    }
    else {
      const stockLevelIds = value
        .map((item) => stockLevelOptions.find((x) => x.name === item)?.id)
        .filter((id) => id !== undefined);
      setStockFilters(stockLevelIds.join(','));
    }
  };

  const handleRecentActivity = (event: React.ChangeEvent<HTMLInputElement>) => {
    setHasRecentActivity(event.target.checked);
  };

  const handleIsMadeInStore = (event: React.ChangeEvent<HTMLInputElement>) => {
    setIsMadeInStore(event.target.checked);
  };

  const handleIsNew = (event: React.ChangeEvent<HTMLInputElement>) => {
    setIsNew(event.target.checked);
  };

  const callLoadDataFromAPI = (options: StockFilterOptions) => {
    setIsLoading(true);
    try {
      props.loadData(options);
    }
    catch (error) {
      logError(error);
    }
    finally {
      setIsLoading(false);
    }
  };

  const currentStoreFilters = storeSelection.mode === 'all' ? '' : storeSelection.storeNumbers.join(',');

  const loadDataFromOrderTable = (fieldToSort: string, sortDirection: string, skip: number, take: number) => {
    callLoadDataFromAPI({
      search,
      fieldToSort,
      sortDirection,
      skip,
      take,
      filters: stockFilters,
      categoryFilters,
      storeFilters: currentStoreFilters,
      hasRecentActivity,
      isMadeInStore,
      isNew: isNew || undefined,
    });
  };

  const clearAll = () => {
    setCategoryFilters('');
    setCategory([]);
    setStock([]);
    setStockFilters('');
    setStoreSelection({ mode: 'all' });
    setHasRecentActivity(false);
    setIsNew(false);
    setSearch('');

    callLoadDataFromAPI({
      search: '',
      fieldToSort: DEFAULT_QUERY.sort,
      sortDirection: DEFAULT_QUERY.direction,
      skip: DEFAULT_QUERY.skip,
      take: DEFAULT_QUERY.take,
      filters: undefined,
      categoryFilters: undefined,
      storeFilters: undefined,
      hasRecentActivity: undefined,
      isMadeInStore: undefined,
      isNew: undefined,
    });
  };

  const searchCall = () => {
    // Reset pagination & force table to re-request page 0 results only for new search
    setSearchTrigger(prev => prev + 1);
    callLoadDataFromAPI({
      search,
      fieldToSort: DEFAULT_QUERY.sort,
      sortDirection: DEFAULT_QUERY.direction,
      skip: DEFAULT_QUERY.skip,
      take: DEFAULT_QUERY.take,
      filters: stockFilters,
      categoryFilters,
      storeFilters: currentStoreFilters,
      hasRecentActivity,
      isMadeInStore,
      isNew: isNew || undefined,
    });
  };

  return (
    <Box
      sx={{
        border: '1px solid',
        borderColor: theme.palette.custom.gray[300],
        py: '0px',
        textAlign: 'left',
      }}>
      <Grid container
        spacing={7}
        sx={{
          py: '12px',
          px: '8px',
        }}>
        <Grid item
          md={6}
          sm={4}
          xs={12}
        >
          <FormControl fullWidth
            size='small'>
            <TextField placeholder={t('stockOnHand.itemNumberOrDescription')}
              size='small'
              id="input-with-icon-textfield"
              label={t('search')}
              variant="outlined"
              InputProps={{
                startAdornment: (
                  <InputAdornment position="start">
                    <SvgIcon><SearchSm /></SvgIcon>
                  </InputAdornment>
                ),
              }}
              value={search}
              onChange={handleSearchChange}
              onKeyDown={handleSearchKeyPress}
            />
          </FormControl>
        </Grid>
        <Grid item
          md={2}
          sm={4}
          xs={12}
        >
          <FormControlLabel
            control={<Switch
              checked={hasRecentActivity}
              onChange={handleRecentActivity}
            />}
            label={t('stockOnHand.recentActivity')}
            labelPlacement="start"
          />
        </Grid>
        <Grid item
          md={2}
          sm={4}
          xs={12}
        >
          <FormControlLabel
            control={<Switch
              checked={isNew}
              onChange={handleIsNew}
            />}
            label={t('stockOnHand.newItem')}
            labelPlacement="start"
          />
        </Grid>
        {hasPermissionToReadMadeInStore && 
        (<Grid item
          md={2}
          sm={4}
          xs={12}
        >
          <FormControlLabel
            control={<Switch
              checked={isMadeInStore}
              onChange={handleIsMadeInStore}
            />}
            label={t('stockOnHand.madeInStore')}
            labelPlacement="start"
          />
        </Grid>)}
        <Grid item
          xs={12}
          md={4}>
          <FormControl fullWidth
            size='small'>
            <InputLabel id="stock-stock-level-label">{t('stockOnHand.stockLevel')}</InputLabel>
            <Select
              labelId="stock-stock-level-label"
              id="stock-stock-level"
              size='small'
              label={t('stockOnHand.stockLevel')}
              multiple
              value={stock}
              onChange={handleStockLevelChange}
              renderValue={(selected) => selected.toString()}
              MenuProps={MenuProps}
            >
              {stockLevelOptions.map((option) => (
                <MenuItem key={option.name}
                  value={option.name}>
                  <Checkbox checked={stock.some(stockLevel =>
                    stockLevel === option.name)} />
                  <ListItemText primary={option.name} />
                </MenuItem>
              ))}
            </Select>
          </FormControl>
        </Grid>
        <Grid item
          xs={12}
          md={4}>
          <FormControl fullWidth
            size='small'>
            <InputLabel id="category-multiple-checkbox-label">{t('stockOnHand.category')}</InputLabel>
            <Select
              labelId="category-multiple-checkbox-label"
              id="category-multiple-checkbox"
              size='small'
              label={t('stockOnHand.category')}
              multiple
              value={category}
              onChange={handleCategoryChange}
              renderValue={(selected) => selected.join(', ')}
              MenuProps={MenuProps}
            >
              {props.stockOnHands?.categories?.filter((name) => name !== null).map((name) => (
                <MenuItem key={name}
                  value={name}>
                  <Checkbox checked={category.indexOf(name) > -1} />
                  <ListItemText primary={name} />
                </MenuItem>
              ))}
            </Select>
          </FormControl>
        </Grid>
        <Grid
          item
          xs={12}
          md={4}>
          <MultiStorePicker
            stores={user?.stores || []}
            value={storeSelection}
            onChange={setStoreSelection}
            allStoresLabel={t('allStores') || 'All Stores'}
            label={t('Store')}
            placeholder={t('search') || 'Search'}
          />
        </Grid>

        <Grid item
          xs={12}
          sm={6}
          md={6}>
          <Button
            fullWidth
            variant="secondary"
            size="lg"
            onClick={clearAll}
          >
            {t('clearAll')}
          </Button>
        </Grid>
        <Grid item
          xs={12}
          sm={6}
          md={6}>
          <Button
            fullWidth
            variant="primary"
            size="lg"
            onClick={searchCall}
            startIcon={<SearchSm />}
          >
            {t('search')}
          </Button>
        </Grid>
        <Grid item
          xs={12}
          sx={{ mt: '20px' }} >
          {
            isLoading ? <LoadingWheel /> : (
              <div style={{ overflowX: 'auto' }}>
                {props.stockOnHands && (
                  <StockOnHandTable
                    list={props.stockOnHands}
                    setPageZero={searchTrigger}
                    loadData={loadDataFromOrderTable}
                  />
                )}
              </div>
            )
          }
        </Grid>
      </Grid>
    </Box>);
};

StockOnHandBox.displayName = 'StockCountDetails';
export default StockOnHandBox;

